import type { Uuid } from "spacetimedb";
import { useQueryClient } from "@tanstack/react-query";
import { Suspense, useMemo, useState } from "react";

import { Button } from "@/web/components/primitives/button";
import { Drawer, DrawerContent, DrawerTitle } from "@/web/components/primitives/drawer";
import { toaster } from "@/web/components/primitives/toast";
import { ErrorHandler } from "@/web/lib/errors";
import { tableQueryOptions, useProcedureMutation, useReducerMutation } from "@/web/lib/tanstack";
import { procedures, reducers, tables } from "@tycho/web-bindings";
import type { TargetablePropertyForUserRow } from "@tycho/web-bindings/types";
import {
  MAX_SELECTIONS,
  MIN_SELECTIONS,
} from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/constants";
import { ExploreMap } from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/ExploreMap";
import type { ExplorePin } from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/types";
import { useExplorePins } from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/useExplorePins";
import { useSelectedBrand } from "@/web/routes/_protected/smb/-dashboard/brandSelection";

// Re-exported for callers (e.g. NegotiateTile) that key off the selection cap.
export { MAX_SELECTIONS } from "@/web/routes/_protected/smb/-dashboard/-actions/-explore/constants";

/**
 * Poll the (subscription-backed) targetable-properties query cache until every
 * `wantedExtIds` row has propagated for the brand, then return their ids keyed
 * by externalId. The SpacetimeDB subscription pushes new rows via `setQueryData`
 * shortly after an add reducer commits; we read the cache directly rather than
 * refetch. Reducers return no payload, so this is the only way to recover the
 * freshly-minted ids.
 */
async function waitForCatalogRows(
  queryClient: ReturnType<typeof useQueryClient>,
  brandId: Uuid,
  wantedExtIds: ReadonlySet<string>,
): Promise<Map<string, Uuid>> {
  const queryKey = tableQueryOptions(tables.allTargetablePropertiesForUser).queryKey;
  const deadline = Date.now() + 5000;

  for (;;) {
    const rows =
      (queryClient.getQueryData(queryKey) as TargetablePropertyForUserRow[] | undefined) ?? [];
    const matched = new Map<string, Uuid>();
    for (const row of rows) {
      if (
        row.brand.compareTo(brandId) === 0 &&
        row.externalId !== undefined &&
        wantedExtIds.has(row.externalId)
      ) {
        matched.set(row.externalId, row.id);
      }
    }
    if (matched.size >= wantedExtIds.size || Date.now() >= deadline) return matched;
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
}

export type ExplorePropertiesDrawerProps = {
  open: boolean;
  /** Underwritable entity the chosen properties get linked to. */
  underwritableEntity: Uuid;
  onOpenChange: (open: boolean) => void;
  onComplete: (result?: unknown) => void;
  /**
   * Targetable-property ids already targeted by the entity. Their pins are
   * hidden from the map so the drawer only ever offers *new* picks. Used when
   * re-opening the drawer to add to an existing selection.
   */
  excludeTargetablePropertyIds?: ReadonlySet<string>;
  /**
   * How many properties the user may pick in this session. Defaults to
   * `MAX_SELECTIONS`; when adding to an existing selection, pass the remaining
   * slots (`MAX_SELECTIONS - currentActiveCount`).
   */
  maxSelections?: number;
};

export function ExplorePropertiesDrawer({
  open,
  underwritableEntity,
  onOpenChange,
  onComplete,
  excludeTargetablePropertyIds,
  maxSelections = MAX_SELECTIONS,
}: ExplorePropertiesDrawerProps) {
  // react-map-gl + React 19 offscreen hazard (read before touching this flow).
  //
  // This drawer hosts a Mapbox map (`ExploreMap`). When a Suspense boundary
  // above the dashboard re-suspends — which happens as `handleConfirm` writes
  // the targetable-property / comps data right as the drawer closes — React 19
  // hides this subtree (offscreen) and later *reconnects* its passive effects.
  // On that reconnect react-map-gl re-runs `<Marker>`/`<Popup>`'s mount effect
  // (`marker.addTo(map.getMap())`) against a mapbox instance whose canvas
  // container has already been torn down, throwing "Cannot read properties of
  // undefined (reading 'appendChild')" and tripping the root error boundary —
  // so the dashboard never gets to render the comps card behind the drawer.
  // Upstream bug, still open: visgl/react-map-gl#2584 and #2588.
  //
  // The crash is neutralized by a patch to `@vis.gl/react-mapbox` that guards
  // those `addTo` calls (see `patches/@vis.gl__react-mapbox@8.1.1.patch`). Note
  // this is NOT comps-specific: any Explore-drawer flow that triggers an
  // offscreen→reappear will hit it. The deeper, still-unanswered follow-up is
  // *why* the dashboard Suspense re-suspends on confirm at all — removing that
  // re-suspend would also kill the map's offscreen flash, but the patch keeps
  // us safe regardless.
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="sm:w-[min(28rem,calc(100dvw-1rem))]">
        {/* Visually-hidden title keeps the drawer accessible; the visible header
            lives inside the map overlay per the design. */}
        <DrawerTitle className="sr-only">Explore properties</DrawerTitle>
        <Suspense fallback={<ExploreMapFallback onClose={() => onOpenChange(false)} />}>
          <ExploreDrawerContent
            underwritableEntity={underwritableEntity}
            onOpenChange={onOpenChange}
            onComplete={onComplete}
            excludeTargetablePropertyIds={excludeTargetablePropertyIds}
            maxSelections={maxSelections}
          />
        </Suspense>
      </DrawerContent>
    </Drawer>
  );
}

function ExploreDrawerContent({
  underwritableEntity,
  onOpenChange,
  onComplete,
  excludeTargetablePropertyIds,
  maxSelections,
}: {
  underwritableEntity: Uuid;
  onOpenChange: (open: boolean) => void;
  onComplete: (result?: unknown) => void;
  excludeTargetablePropertyIds?: ReadonlySet<string>;
  maxSelections: number;
}) {
  const queryClient = useQueryClient();
  // Scoped to the brand chosen in the dashboard's brand switcher (the sole brand
  // for single-brand users). See `brandSelection.tsx`.
  const selectedBrand = useSelectedBrand();
  const selectedBrandId = selectedBrand?.id ?? null;

  const { pins: basePins } = useExplorePins({
    brandId: selectedBrandId,
    excludeTargetablePropertyIds,
  });

  // Searched (off-catalog) pins the user resolved via the address bar. Kept in
  // local state so they persist on the map until confirm; merged ahead of the
  // base pins so a just-searched property shows first.
  const [searchedPins, setSearchedPins] = useState<ExplorePin[]>([]);
  const pins = useMemo(() => {
    // De-dupe a searched pin that also exists in the base set (same key).
    const baseKeys = new Set(basePins.map((p) => p.key));
    return [...searchedPins.filter((p) => !baseKeys.has(p.key)), ...basePins];
  }, [searchedPins, basePins]);

  // Staged selection, keyed by pin key. Persisted to the server only on confirm.
  const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(new Set());
  const totalSelected = selectedKeys.size;
  const atCap = totalSelected >= maxSelections;

  const { mutateAsync: addTargetableProperty } = useReducerMutation(
    reducers.addTargetablePropertyForBrand,
  );
  const { mutateAsync: targetProperty } = useProcedureMutation(
    procedures.targetPropertyForUwEntity,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggle = (pin: ExplorePin) => {
    const next = new Set(selectedKeys);
    if (next.has(pin.key)) next.delete(pin.key);
    else if (!atCap) next.add(pin.key);
    setSelectedKeys(next);
  };

  const onSearchSelect = (pin: ExplorePin) => {
    setSearchedPins((prev) => (prev.some((p) => p.key === pin.key) ? prev : [pin, ...prev]));
    if (!atCap) setSelectedKeys((prev) => new Set(prev).add(pin.key));
  };

  const handleConfirm = async () => {
    if (selectedBrandId === null || totalSelected < MIN_SELECTIONS) return;
    setIsSubmitting(true);
    try {
      const chosen = pins.filter((p) => selectedKeys.has(p.key));

      // 1) Register chosen Catskills + searched properties into the brand
      //    catalog (idempotent by externalId). Operating locations are already
      //    in the catalog, so they skip this step.
      const toAdd = chosen.filter(
        (p) => p.source.kind === "catskills" || p.source.kind === "searched",
      );
      for (const pin of toAdd) {
        if (pin.source.kind === "operating") continue;
        await addTargetableProperty({
          brand: selectedBrandId,
          googlePlaceId: undefined,
          googlePlacePhotoName: undefined,
          imageUrl: pin.imageUrl,
          source: pin.source.kind === "catskills" ? { tag: "Catskills" } : { tag: "Manual" },
          externalId: pin.source.externalId,
          latitude: pin.latitude,
          longitude: pin.longitude,
          canonicalAddress: pin.address,
        });
      }

      // 2) Recover freshly-minted catalog ids (subscription-backed; poll the
      //    cache) keyed by externalId. The add reducers above have already
      //    committed server-side, so a slow subscription push must NOT abort the
      //    whole confirm — we proceed with whatever ids resolved and only flag
      //    the laggards. (The rows persist; re-confirming is idempotent.)
      const wantedExtIds = new Set(
        toAdd.map((p) => (p.source.kind === "operating" ? "" : p.source.externalId)),
      );
      const addedIds =
        wantedExtIds.size === 0
          ? new Map<string, Uuid>()
          : await waitForCatalogRows(queryClient, selectedBrandId, wantedExtIds);

      // 3) Resolve every chosen pin to a targetable-property id, then target it.
      //    `targetPropertyForUwEntity` is idempotent + additive. A pin whose
      //    catalog row hasn't propagated yet resolves to undefined and is
      //    deferred (reported below) rather than blocking the rest.
      const targetIds = chosen
        .map((pin) =>
          pin.source.kind === "operating"
            ? pin.source.targetablePropertyId
            : addedIds.get(pin.source.externalId),
        )
        .filter((id): id is Uuid => id !== undefined);

      // Target each property independently: one transient failure (or the
      // server cap) must not strand the properties that would otherwise succeed.
      // We collect outcomes and report honestly instead of leaving the drawer in
      // an all-or-nothing limbo.
      const succeeded: Uuid[] = [];
      const failed: Uuid[] = [];
      for (const targetableProperty of targetIds) {
        try {
          await targetProperty({ underwritableEntity, targetableProperty });
          succeeded.push(targetableProperty);
        } catch (error) {
          ErrorHandler.default(error);
          failed.push(targetableProperty);
        }
      }

      const unresolved = wantedExtIds.size - addedIds.size;
      if (failed.length > 0 || unresolved > 0) {
        toaster({
          message:
            succeeded.length > 0
              ? "Some properties couldn't be added — they're still selected, try Confirm again."
              : "Couldn't add your properties. Please try again.",
          variant: "error",
        });
        // Drop the successfully-targeted pins from the staged selection so a
        // retry only re-attempts the ones that didn't land.
        if (succeeded.length > 0) {
          const targeted = new Set(succeeded.map((id) => id.toString()));
          setSelectedKeys((prev) => {
            const next = new Set(prev);
            for (const pin of chosen) {
              const id =
                pin.source.kind === "operating"
                  ? pin.source.targetablePropertyId
                  : addedIds.get(pin.source.externalId);
              if (id && targeted.has(id.toString())) next.delete(pin.key);
            }
            return next;
          });
        }
        setIsSubmitting(false);
        return;
      }

      onOpenChange(false);
      onComplete({ targetableProperties: succeeded });
    } catch (error) {
      ErrorHandler.default(error);
      setIsSubmitting(false);
    }
  };

  return (
    <ExploreMap
      pins={pins}
      selectedKeys={selectedKeys}
      onToggle={toggle}
      onSearchSelect={onSearchSelect}
      atCap={atCap}
      selectedCount={totalSelected}
      maxSelections={maxSelections}
      onClose={() => onOpenChange(false)}
      footer={
        <Button
          color="red"
          size="lg"
          className="w-full"
          disabled={totalSelected < MIN_SELECTIONS || isSubmitting}
          isLoading={isSubmitting}
          onClick={handleConfirm}
        >
          Confirm
        </Button>
      }
    />
  );
}

function ExploreMapFallback({ onClose }: { onClose: () => void }) {
  return (
    <div className="relative flex h-full w-full items-center justify-center bg-withus-dark-blue/5">
      <button
        type="button"
        onClick={onClose}
        className="absolute top-5 right-5 text-withus-dark-blue"
        aria-label="Close"
      >
        ✕
      </button>
      <span className="text-sm text-muted-foreground">Loading map…</span>
    </div>
  );
}
